import { useCallback } from 'react'
import conf from '~/app/conf'
import useScriptLoader from '~/app/utils/useScriptLoader'
import QueryString from 'qs'
import copy from 'copy-to-clipboard'
import UAParser from 'ua-parser-js'
import { getYaNative } from '@yanolja-rnd/ya-native'
import { isNative } from '~/app/utils/typeguards'

/**
 * useShare
 * author: soojin.oh
 */

// type definitions

// hook
const useShare = () => {
  const scriptUrl = '//developers.kakao.com/sdk/js/kakao.min.js'
  const onLoad = useCallback(() => {
    if (window && typeof Kakao !== 'undefined' && !Kakao.isInitialized()) {
      Kakao?.init(conf.kakao.appKey)
    }
  }, [])

  useScriptLoader(scriptUrl, onLoad, 'kakaolink')

  const kakaoShare = useCallback((url: string, installTalk?: boolean) => {
    const ogImageUrl = 'https://yaimg.yanolja.com/joy/sunny/static/images/nol/nol_og_800x800.png'
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.shareContent({
        sendTo: 'kakao',
        data: {
          /**
           * todo yaNative 를 통한 카카오 공유하기 기능에는 content 항목이 제공되지 않아서 title 에 포함함.
           *  CX 에 description 를 전달할 수 있도록 작업 요청이 필요해 보임
           */
          title: '[NOL 예약] 놀수록 놀라운 세상',
          imgUrl: ogImageUrl,
          webUrl: url,
          mobileWebUrl: url,
        },
      })
    } else {
      const shareKakaoContent: Kakao.Link.DefaultFeedSettings = {
        objectType: 'feed',
        content: {
          title: '[NOL 예약]',
          description: '놀수록 놀라운 세상',
          imageUrl: ogImageUrl,
          imageWidth: 800,
          imageHeight: 800,
          link: {
            webUrl: url,
            mobileWebUrl: url,
          },
        },
        installTalk,
      }
      Kakao.Link.sendDefault(shareKakaoContent)
    }
  }, [])

  const facebookShare = useCallback((url: string) => {
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.shareContent({
        sendTo: 'facebook',
        data: {
          url,
        },
      })
    } else {
      const queryString = QueryString.stringify({ u: url })
      const openUrl = `https://www.facebook.com/sharer.php?${queryString}`
      window.open(openUrl, 'popup')
    }
  }, [])

  const smsShare = useCallback((url: string) => {
    // sms 줄바꿈 문자는 %0a
    const body = `[야놀자 예약]%0a이제 해외여행도 놀자. 계산적으로.]%0a%0a${url}`
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      if (yaNative.enabledFor === 'ios') {
        window.location.href = `sms:&body=${body}`
      } else {
        window.location.href = `sms:?body=${body}`
      }
    } else {
      const iOS = new UAParser().getOS().name === 'iOS'
      if (iOS) {
        window.location.href = `sms:&body=${body}`
      } else {
        window.location.href = `sms:?body=${body}`
      }
    }
  }, [])

  const linkShare = useCallback(async (url: string) => {
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.shareContent({
        sendTo: 'clipboard',
        data: {
          content: url,
        },
      })
    } else {
      await copy(url)
    }
  }, [])

  return { kakaoShare, facebookShare, smsShare, linkShare }
}

export default useShare
